import { FlipWords } from "@/components/ui/flip-words";
import { useSuggestedQueries } from "@brandclub/common-ui";
import { useTheme } from "@mui/material";
import { Sparkles } from "lucide-react";
import React, { memo } from "react";
import { AnimatedMasonryGrid } from "./AnimatedMasonryGrid";
import { ConciergeWelcomeEmbedded } from "./ConciergeWelcomeEmbedded";
import { usePageContext } from "../hooks/usePageContext";

interface ConciergeWelcomeProps {
  trendingSearches?: Array<{ text: string }>;
  onSearch: (searchTerm: string) => void;
  brandId?: number;
  isEmbedded?: boolean;
}

export function FlipWordsDemo() {
  const words = [
    "find the perfect gift for your mom",
    "save on your weekly grocery shopping",
    "find a Halloween costume for your dog",
  ];
  const theme = useTheme();

  return (
    <div
      className="tw:text-4xl tw:font-bold tw:text-left tw:max-w-[350px] tw:md:max-w-[500px] tw:h-[200px] tw:md:h-[200px] tw:pl-[18px] tw:md:pl-[43px]"
      style={{
        color: theme.palette.text.primary,
      }}
    >
      Use Concierge to <FlipWords words={words} duration={4_000} />
    </div>
  );
}

const ConciergeWelcomeComponent = ({
  trendingSearches,
  onSearch,
  brandId,
  isEmbedded = false,
}: ConciergeWelcomeProps) => {
  const { data, isLoading: isLoadingSuggestedQueries } =
    useSuggestedQueries(brandId);

  const theme = useTheme();

  // Memoize displayQueries to prevent recalculation on every render
  const displayQueries = React.useMemo(
    () =>
      data?.suggestedQuestions ||
      trendingSearches?.map((search) => search.text) ||
      [],
    [data?.suggestedQuestions, trendingSearches]
  );

  // Get page context (always call hook, but only use when embedded)
  const pageContext = usePageContext();

  // Custom welcome screen for embedded mode
  if (isEmbedded) {
    return (
      <ConciergeWelcomeEmbedded
        displayQueries={displayQueries}
        isLoadingSuggestedQueries={isLoadingSuggestedQueries}
        onSearch={onSearch}
        pageContext={pageContext}
      />
    );
  }

  // Full welcome screen for non-embedded mode
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        textAlign: "center",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Heading with better typography */}
      <FlipWordsDemo />

      {/* Masonry Grid Section */}
      {displayQueries.length > 0 && !isLoadingSuggestedQueries && (
        <div style={{ width: "100%", maxWidth: "1200px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "28px",
            }}
          >
            <Sparkles
              style={{
                width: "18px",
                height: "18px",
                color: theme.palette.text.secondary,
              }}
            />
            <h3
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: theme.palette.text.secondary,
                margin: 0,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              For you
            </h3>
          </div>
          <AnimatedMasonryGrid items={displayQueries} onItemClick={onSearch} />
        </div>
      )}
    </div>
  );
};

// Memoize to prevent re-renders when parent re-renders due to input changes
const ConciergeWelcome = memo(ConciergeWelcomeComponent);

export default ConciergeWelcome;
